import { api } from './api';

// JWT token management
export const authUtils = {
  // Store JWT token
  setToken: (token: string) => {
    localStorage.setItem('jwt_token', token);
  },

  // Get JWT token
  getToken: () => {
    return localStorage.getItem('jwt_token');
  },

  // Remove JWT token
  removeToken: () => {
    localStorage.removeItem('jwt_token');
  },

  // Check if user is authenticated
  isAuthenticated: () => {
    const token = authUtils.getToken();
    if (!token) return false;

    // Check if token is expired (basic JWT validation)
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Date.now() / 1000;
      return payload.exp > currentTime;
    } catch (error) {
      // If token is malformed, clear it and return false
      console.warn('Invalid JWT token format:', error);
      authUtils.removeToken();
      authUtils.removeUserEmail();
      return false;
    }
  },

  // Store user email
  setUserEmail: (email: string) => {
    localStorage.setItem('user_email', email);
  },

  // Get user email
  getUserEmail: () => {
    return localStorage.getItem('user_email');
  },

  // Remove user email
  removeUserEmail: () => {
    localStorage.removeItem('user_email');
  },

  // Clear all auth data
  clearAuth: () => {
    authUtils.removeToken();
    authUtils.removeUserEmail();
  },

  // Login user
  login: async (email: string, password: string) => {
    try {
      const response = await api.post('/api/auth/optimove/login', { email, password });
      const { token } = response.data.data;

      if (token) {
        authUtils.setToken(token);
        authUtils.setUserEmail(email);
        return { success: true, data: response.data.data };
      } else {
        return { success: false, error: 'No token received' };
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Login failed',
      };
    }
  },

  // Register user
  register: async (name: string, company: string, email: string, password: string) => {
    try {
      const response = await api.post('/api/auth/optimove/register', {
        name,
        company,
        email,
        password,
      });
      return { success: true, data: response.data.data };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Registration failed',
      };
    }
  },

  // Logout user
  logout: () => {
    try {
      authUtils.clearAuth();
    } catch (error) {
      console.error('Logout error:', error);
      // Still try to clear auth data even if there's an error
      authUtils.clearAuth();
    }
  },
};

export default authUtils;
